import useResolveAppBuilderSettings from "@AppBuilderLib/features/appbuilder/model/useResolveAppBuilderSettings";
import useQuerySavedState from "@AppBuilderLib/features/model-state/model/useQuerySavedState";
import {
	QUERYPARAM_CONTEXT,
	QUERYPARAM_DISABLEFALLBACKUI,
	QUERYPARAM_JWTTOKEN,
	QUERYPARAM_MODELSTATEID,
	QUERYPARAM_MODELVIEWURL,
	QUERYPARAM_PARAMVALUE_PREFIX,
	QUERYPARAM_PLATFORMURL,
	QUERYPARAM_SAVEDSTATEID,
	QUERYPARAM_SETTINGSURL,
	QUERYPARAM_SLUG,
	QUERYPARAM_TEMPLATE,
	QUERYPARAM_TICKET,
} from "@AppBuilderLib/shared/config/queryparams";
import {Logger} from "@AppBuilderLib/shared/lib/logger";
import {getDefaultPlatformUrl} from "@AppBuilderLib/shared/lib/platform/environment";
import useAsync from "@AppBuilderLib/shared/lib/useAsync";
import {useThemeOverrideStore} from "@AppBuilderLib/shared/model/useThemeOverrideStore";
import {
	MantineThemeComponent,
	MantineThemeOverride,
	useProps,
} from "@mantine/core";
import {useCallback, useEffect, useMemo} from "react";
import {
	IAppBuilderSettings,
	IAppBuilderSettingsJsonSession,
	IAppBuilderSettingsSession,
} from "../config/appbuilder";
import {parseAppBuilderSettingsJson} from "../config/parseAppBuilderJson";

declare global {
	interface Window {
		/**
		 * Update the theme at runtime for testing purposes.
		 * Accepts an app settings object with a `themeOverrides` property (i.e. the full JSON file).
		 */
		updateTheme: (settings: {
			themeOverrides?: MantineThemeOverride;
		}) => void;
	}
}

/**
 * Test a string value for being "true" or "1".
 * @param value
 * @returns
 */
function isTrueish(value: string | null | undefined) {
	return value === "true" || value === "1";
}

/**
 * Theme property which allows to define default sessions.
 * These sessions will be used in addition to sessions defined via JSON or URL query parameters.
 */
interface DefaultSessionsThemePropsType {
	sessions?: IAppBuilderSettingsJsonSession[];
}

export function DefaultSessionsThemeProps(
	props: DefaultSessionsThemePropsType,
): MantineThemeComponent {
	return {
		defaultProps: props,
	};
}

/**
 * Load settings for the app builder from a JSON file defined by an URL query parameter.
 * As an alternative, use URL query parameters to define the session directly, based on
 *   * ticket and modelViewUrl, or
 *   * slug and platformUrl.
 *
 * @param defaultSession Default session definition to use if parameters could not be loaded.
 * @param queryParamName Name of the query parameter to use for loading settings json.
 * @returns
 */
export default function useAppBuilderSettings(
	defaultSession?: IAppBuilderSettingsJsonSession,
	queryParamName = QUERYPARAM_SETTINGSURL,
) {
	const parameters = useMemo<URLSearchParams>(
		() => new URLSearchParams(window.location.search),
		[],
	);

	// try to load settings json
	const url = parameters.get(queryParamName);
	const {value, error, loading} = useAsync(async () => {
		if (!url) return;
		const response = await fetch(url, {mode: "cors"});

		return parseAppBuilderSettingsJson(await response.json());
	}, [url]);

	// check for ticket, modelViewUrl, slug and platformUrl
	const ticket = parameters.get(QUERYPARAM_TICKET);
	const modelViewUrl = parameters
		.get(QUERYPARAM_MODELVIEWURL)
		?.replace(/\/+$/, "");
	const jwtToken = parameters.get(QUERYPARAM_JWTTOKEN) ?? undefined;
	const slug = parameters.get(QUERYPARAM_SLUG);
	const platformUrl = parameters
		.get(QUERYPARAM_PLATFORMURL)
		?.replace(/\/+$/, "");
	const disableFallbackUi = isTrueish(
		parameters.get(QUERYPARAM_DISABLEFALLBACKUI),
	);
	const template = parameters.get(QUERYPARAM_TEMPLATE);
	const modelStateId =
		parameters.get(QUERYPARAM_MODELSTATEID) !== null
			? parameters.get(QUERYPARAM_MODELSTATEID)!
			: undefined;
	const context = parameters.get(QUERYPARAM_CONTEXT);
	const savedStateId = parameters.get(QUERYPARAM_SAVEDSTATEID);
	const {initialSavedState} = useQuerySavedState(savedStateId);
	const initialParameterValues = useMemo(
		() =>
			initialSavedState.status === "success" &&
			initialSavedState.data?.parameters
				? Object.keys(initialSavedState.data.parameters).reduce(
						(o, key) => {
							o[key] =
								initialSavedState.data!.parameters[
									key
								].toString();
							return o;
						},
						{} as {[key: string]: string},
					)
				: undefined,
		[initialSavedState],
	);

	const queryParamSession = useMemo<IAppBuilderSettingsSession | undefined>(
		() =>
			slug
				? ({
						id: "default",
						slug,
						platformUrl: platformUrl ?? getDefaultPlatformUrl(),
						modelStateId,
					} as IAppBuilderSettingsSession)
				: ticket && modelViewUrl
					? {
							id: "default",
							ticket,
							modelViewUrl,
							jwtToken,
							modelStateId,
						}
					: undefined,
		[slug, platformUrl, ticket, modelViewUrl, jwtToken, modelStateId],
	);

	// get all query parameters starting with QUERYPARAM_PARAMVALUE_PREFIX
	const paramValues = new Map<string, string>();
	parameters.forEach((value, key) => {
		if (key.startsWith(QUERYPARAM_PARAMVALUE_PREFIX))
			paramValues.set(key, value);
	});

	// define theme overrides based on query string params
	const themeOverrides = useMemo(() => {
		return template
			? {
					components: {
						AppBuilderTemplateSelector: {
							defaultProps: {
								template: template,
							},
						},
					},
				}
			: undefined;
	}, [template]);

	// get further optional session definitions from theme
	const {sessions: themeSessions} = useProps(
		"DefaultSessions",
		{},
		{} as DefaultSessionsThemePropsType,
	);

	// create the session array in a memo, otherwise it is recreated every time
	const sessionsArray = useMemo<
		IAppBuilderSettingsJsonSession[] | undefined
	>(() => {
		if (loading) return undefined;

		if (!value) {
			// No JSON loaded, use query params or default session
			const session = defaultSession || queryParamSession;

			if (!session) return themeSessions;

			const mergedSession: IAppBuilderSettingsJsonSession = {
				...session,
			};

			if (modelStateId && !mergedSession.modelStateId)
				mergedSession.modelStateId = modelStateId;

			return [mergedSession, ...(themeSessions ?? [])];
		} else {
			// JSON loaded, combine with query params/default session
			const session = defaultSession || queryParamSession;
			const {sessions} = value;

			let combinedSessions: IAppBuilderSettingsJsonSession[] = [];
			if (session) combinedSessions.push(session);
			if (sessions) combinedSessions = combinedSessions.concat(sessions);

			// ensure modelStateId is set on the first session
			// if it was defined via query params and was not set yet
			if (
				modelStateId &&
				combinedSessions.length > 0 &&
				!combinedSessions[0].modelStateId
			) {
				combinedSessions[0].modelStateId = modelStateId;
			}

			return combinedSessions.concat(themeSessions ?? []);
		}
	}, [
		loading,
		value,
		defaultSession,
		queryParamSession,
		modelStateId,
		themeSessions,
	]);

	// create the settings object, either with the json data or without
	const settings = useMemo<IAppBuilderSettings | undefined>(() => {
		if (!sessionsArray) return undefined;
		if (loading) return undefined;

		if (!value) {
			return {
				version: "1.0",
				sessions: sessionsArray,
				settings: {disableFallbackUi},
				themeOverrides: themeOverrides,
			};
		} else {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const {sessions: _, ...rest} = value;
			return {
				sessions: sessionsArray,
				...rest,
			};
		}
	}, [loading, sessionsArray, value, disableFallbackUi, themeOverrides]);

	// register theme overrides
	const setThemeOverride = useThemeOverrideStore(
		(state) => state.setThemeOverride,
	);
	useEffect(() => {
		Logger.debug("Theme overrides", value);
		setThemeOverride(settings?.themeOverrides);
	}, [settings?.themeOverrides]);

	// expose window.updateTheme for runtime testing
	const updateTheme = useCallback(
		(input: {themeOverrides?: MantineThemeOverride}) => {
			setThemeOverride(input.themeOverrides ?? {});
		},
		[setThemeOverride],
	);
	useEffect(() => {
		window.updateTheme = updateTheme;

		return () => {
			delete (window as Partial<Window>).updateTheme;
		};
	}, [updateTheme]);

	const {
		settings: resolvedSettings,
		error: resolveError,
		loading: resolveLoading,
	} = useResolveAppBuilderSettings(settings);

	// add the initial parameter values of the resolved saved state to the first session
	if (initialParameterValues && resolvedSettings?.sessions?.length) {
		const session = resolvedSettings.sessions[0];
		if (!session.initialParameterValues)
			session.initialParameterValues = {};
		Object.assign(session.initialParameterValues, initialParameterValues);
	}

	// add context as an initial parameter value to all sessions
	if (context && resolvedSettings?.sessions) {
		for (let i = 0; i < resolvedSettings.sessions.length; i++) {
			const session = resolvedSettings.sessions[i];
			if (!session.initialParameterValues)
				session.initialParameterValues = {};
			session.initialParameterValues["context"] = context;
		}
	}

	// add parameter values defined in query string to all sessions
	if (paramValues.size > 0 && resolvedSettings?.sessions) {
		for (let i = 0; i < resolvedSettings.sessions.length; i++) {
			const session = resolvedSettings.sessions[i];
			if (!session.initialParameterValues)
				session.initialParameterValues = {};
			paramValues.forEach((value, key) => {
				session.initialParameterValues![
					key.substring(QUERYPARAM_PARAMVALUE_PREFIX.length)
				] = value;
			});
		}
	}

	// Withhold the resolved settings while the initial saved state is still
	// loading, so the page does not start a viewer session before the saved
	// state's initial parameter values are available.
	const publicSettings =
		initialSavedState.status === "loading" ? undefined : resolvedSettings;

	return {
		settings: publicSettings,
		error: error || resolveError,
		loading:
			loading || resolveLoading || initialSavedState.status === "loading",
		hasSettings: parameters.size > 0,
		hasSession:
			(settings?.sessions && settings.sessions.length > 0) ||
			(publicSettings?.sessions && publicSettings.sessions.length > 0),
	};
}
